import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, IndianRupee } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { PageHeader } from "@/components/dashboard/ui";

export default function AdminPricing() {
  const [plans, setPlans] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/plans").then((r) => {
    setPlans(r.data.plans);
    setDraft(Object.fromEntries(r.data.plans.map((p) => [p.id, p.price])));
  }).catch(() => setPlans([]));
  useEffect(() => { load(); }, []);
  if (!plans) return <Loader />;

  const save = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/plans/${id}/price`, { price: Number(draft[id]) }); toast.success("Price updated — reflected everywhere instantly"); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Pricing" subtitle="Set the monthly price (₹) for each plan. Features are fixed." />
      <div className="space-y-4">
        {plans.map((p) => (
          <div key={p.id} data-testid={`admin-plan-${p.id}`} className="rounded-2xl border border-border bg-black/40 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2"><span className="text-white font-medium text-lg">{p.name}</span>{p.popular && <span className="text-[10px] bg-gold text-black px-2 py-0.5 rounded-full">Popular</span>}</div>
              <div className="text-sm text-muted-foreground mt-1">{p.tagline}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <IndianRupee className="w-4 h-4 text-gold absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid={`price-input-${p.id}`} type="number" min={0} value={draft[p.id] ?? ""} onChange={(e) => setDraft({ ...draft, [p.id]: e.target.value })}
                  className="w-40 bg-secondary border border-input rounded-xl pl-9 pr-3 py-2.5 text-white outline-none focus:border-gold" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">/mo</span>
              </div>
              <button data-testid={`save-price-${p.id}`} onClick={() => save(p.id)} disabled={busy === p.id || Number(draft[p.id]) === p.price}
                className="inline-flex items-center gap-2 bg-gold text-black text-sm font-medium rounded-full px-5 py-2.5 hover:bg-gold-light disabled:opacity-50">
                {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-6">Updated prices appear on the landing page, pricing page and photographer subscription screens immediately.</p>
    </div>
  );
}
